import React, { useState } from 'react';
import {
    Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton, Dropdown, IDropdownOption,
    TextField, Stack,
} from '@fluentui/react';
import { DISMISS_REASON_LABELS } from '../../models/BundleSuggestions';

interface IProps {
    onCancel: () => void;
    onConfirm: (reason?: string, detail?: string) => void;
}

const REASON_OPTIONS: IDropdownOption[] = Object.entries(DISMISS_REASON_LABELS).map(([key, text]) => ({ key, text }));

/**
 * Shared reason-capture dialog for dismissing a bundle suggestion, opened from both the suggestion
 * card's X icon and the details panel's Dismiss button. Only collects reason/detail - the caller is
 * responsible for actually calling the dismiss mutation on confirm.
 */
const DismissBundleSuggestionDialog: React.FC<IProps> = (props) => {
    const [reason, setReason] = useState<string | undefined>(undefined);
    const [detail, setDetail] = useState<string>('');

    const isOther = reason === 'other';
    const canConfirm = !!reason && (!isOther || detail.trim().length > 0);

    const confirm = () => {
        props.onConfirm(reason, detail.trim() ? detail.trim() : undefined);
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={props.onCancel}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Dismiss Suggestion',
                subText: "Why doesn't this bundle work? This helps improve future suggestions.",
            }}
            minWidth={440}
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                <Dropdown
                    label='Reason'
                    placeholder='Select a reason'
                    selectedKey={reason}
                    options={REASON_OPTIONS}
                    onChange={(_, option) => setReason(option?.key as string)}
                />
                {isOther &&
                    <TextField
                        label='Tell us more'
                        multiline
                        rows={3}
                        required
                        value={detail}
                        onChange={(_, v) => setDetail(v || '')}
                    />
                }
            </Stack>

            <DialogFooter>
                <PrimaryButton text='Dismiss' disabled={!canConfirm} onClick={confirm} />
                <DefaultButton text='Cancel' onClick={props.onCancel} />
            </DialogFooter>
        </Dialog>
    );
};

export default DismissBundleSuggestionDialog;
